import { useCallback, useEffect, useRef, useState } from 'react';
import { simulationDatabaseHistory } from '../../Utils/simulation_database_utils';
import controllerOnMessage from '../../websocket/controllerOnMessageHook';

let nextRequest = 0;

// One request at a time shares the existing controller response mailbox. Graph
// detail never replaces the page result, its cursor, or its irrigation totals.
export default function useDatabaseHistory({ manager, controller, id, response, command, period, section, includeSensors }) {
  const [rows, setRows] = useState([]);
  const [summaryResult, setSummaryResult] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [knownZones, setKnownZones] = useState({});
  const [beforeId, setBeforeId] = useState(null);
  const [status, setStatus] = useState('idle');
  const [failedInserts, setFailedInserts] = useState(0);
  const pending = useRef(null);
  const details = useRef([]);
  const historyController = useRef(id);
  const timeout = useRef(null);

  const send = useCallback(function send(entry) {
    const requestId = `${Date.now()}-${++nextRequest}`;
    const finish = (response) => {
      const current = pending.current;
      clearTimeout(timeout.current);
      const delay = [2000, 5000, 10000][entry.attempt ?? 0];
      if (['unavailable', 'timeout', 'offline'].includes(response.status) && delay !== undefined &&
          (!current.detail || current.complete)) {
        // Keep the original period/cursor, invalidate the expired request ID,
        // and reuse the one request timer for bounded backoff.
        pending.current = { ...current, id: null };
        if (!current.detail) setStatus('retrying');
        timeout.current = setTimeout(() => send({ ...pending.current, attempt: (entry.attempt ?? 0) + 1 }), delay);
        return;
      }
      pending.current = null;
      if (current.detail) current.complete?.(response);
      else {
        setStatus(response.status);
        if (response.status === 'ok') {
          const reduced = response.query_version === 1 ? response : null;
          setSummaryResult(reduced ? { data: reduced, period: current.period } : null);
          setEventData(response.events_query_version === 1 ? response : null);
          if (reduced) setKnownZones((previous) => ({ ...previous, ...Object.fromEntries(reduced.series.map((item) => [String(item.zone), item.name || previous[item.zone] || ''])) }));
          setRows((previous) => current.append ? [...previous, ...(response.rows ?? [])] : [...(response.rows ?? [])]);
          setBeforeId(response.next_before_id);
          setFailedInserts(response.failed_inserts);
        }
      }
      if (details.current.length) send(details.current.shift());
    };
    pending.current = { ...entry, id: requestId, finish };
    timeout.current = setTimeout(() => finish({ status: 'timeout' }), 10000);
    try {
      const query = { ...entry.query, request_id: requestId };
      if (controller.simulation) {
        controllerOnMessage({ store: manager }, { command: 'send_database_logging', internal_id: id,
          data: simulationDatabaseHistory(controller, query) });
        return;
      }
      if (manager.wsManager.sendWSMsg(controller, 'request_database_logging', query, true, false, true)) return;
    } catch (_) { /* Same visible failure as a closed socket. */ }
    finish({ status: 'offline' });
  }, [manager, controller, id]);

  const cancel = useCallback(() => {
    clearTimeout(timeout.current);
    if (pending.current?.detail) pending.current.complete?.({ status: 'cancelled' });
    pending.current = null;
    details.current.forEach((entry) => entry.complete?.({ status: 'cancelled' }));
    details.current = [];
  }, []);

  const request = useCallback((before = null) => {
    cancel();
    if (historyController.current !== id) {
      historyController.current = id;
      setRows([]); setSummaryResult(null); setEventData(null); setKnownZones({});
    }
    if (before === null) {
      if (command !== 'update_values') setRows([]);
      setBeforeId(null);
    }
    let queryOptions = {};
    if (period) {
      const { from, to, zone } = period;
      if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from || to - from > 180 * 86400000) {
        setStatus('invalid_request'); return;
      }
      queryOptions = command === 'events' ? { view: 'events', period: { from, to }, zone, event_type: period.event_type, actor: period.actor } :
        { view: 'measurements', period: { from, to }, zone, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          points: section === 'dashboard' ? 120 : 600, include_sensors: section !== 'dashboard' && includeSensors,
          include_activities: section !== 'dashboard', include_equipment: section !== 'dashboard',
          ...(section === 'dashboard' ? { main_fields: ['temp', 'humidity', 'co2', 'vol_water_content', 'ec_root_zone'] } : {}) };
    }
    setStatus('loading');
    send({ period, append: before !== null, query: {
      command: command === 'events' ? '' : command, ...queryOptions, ...(before ? { before_id: before } : {})
    } });
  }, [cancel, send, id, command, period, section, includeSensors]);

  useEffect(() => {
    request();
    return cancel;
  }, [request, cancel]);

  useEffect(() => {
    if (!pending.current || response?.request_id !== pending.current.id) return;
    pending.current.finish(response);
  }, [response]);

  // Match the original request: retention may shorten the returned period.
  const summaryData = historyController.current === id && period &&
    summaryResult?.period?.from === period.from && summaryResult?.period?.to === period.to &&
    summaryResult?.period?.zone === period.zone ? summaryResult.data : null;

  useEffect(() => {
    if (command !== 'update_values' || status !== 'ok' || !(summaryData?.indexing || summaryData?.projection_error)) return;
    const retry = setTimeout(() => request(), 5000);
    return () => clearTimeout(retry);
  }, [command, status, summaryData?.indexing, summaryData?.projection_error, request]);

  const requestDetail = useCallback(({ from, to, zone, points }, complete) => {
    const entry = { detail: true, complete, query: { command: 'update_values', view: 'measurements',
      period: { from: Math.floor(from / 60000) * 60000, to: Math.ceil(to / 60000) * 60000 }, zone, points,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      include_sensors: includeSensors, include_activities: true, include_equipment: true } };
    if (pending.current) details.current.push(entry);
    else send(entry);
    return () => {
      // Let an already sent query finish before sending the next one. This also
      // bounds database work during repeated gestures; its result is discarded.
      entry.complete = null;
      details.current = details.current.filter((queued) => queued !== entry);
      if (pending.current?.detail && pending.current.complete === complete) {
        pending.current.complete = null;
        if (pending.current.id === null) {
          clearTimeout(timeout.current);
          pending.current = null;
          if (details.current.length) send(details.current.shift());
        }
      }
    };
  }, [send, includeSensors]);

  return { rows, summaryData, hasSummary: Boolean(summaryResult), eventData, knownZones, beforeId,
    status, failedInserts, request, requestDetail, clearResults: () => { setSummaryResult(null); setEventData(null); } };
}
